import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IdTaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { restoreArchivedTask } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { ModalDialog } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import { useSuccessNotification } from 'src/hooks/useSuccessNotification';

interface RestoreModalProps {
  state: ModalStateOpen<IdTaskDto>;
  reloadTasks: () => void;
}

export const RestoreModal: React.FC<RestoreModalProps> = React.memo(function RestoreModal({ state, reloadTasks }) {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard']);
  const onFail = useRequestErrorNotification();

  const restoreRequest = useLazyRequest({
    apiMethod: restoreArchivedTask,
    onSuccess: useSuccessNotification(t('serviceDashboard:TASK_WAS_RESTORED')),
    onFail,
  });
  const restoring = useSubscribe(restoreRequest.pending$);

  const restoreTask = useCallbackRef(async () => {
    if (state.payload) {
      try {
        await restoreRequest.load({ taskId: state.payload });
        reloadTasks();
      } finally {
        state.close();
      }
    }
  });

  return (
    <ModalDialog
      state={state}
      title={t('serviceDashboard:RESTORE_TASK')}
      text={t('serviceDashboard:RESTORE_TASK_CONFIRM')}
      onActionButtonClick={restoreTask}
      cancelButtonText={t('common:CANCEL')}
      actionButtonText={t('common:BUTTON_CONFIRM')}
      isPendingAction={restoring}
      dataTestId='restore-modal-buttons'
    />
  );
});
